/**
 * 受信者のプライバシー保護（リモート画像の取り扱い）の語彙・契約・純関数。
 *
 * ## 何を守るのか
 *
 * HTML メールに `<img src="https://tracker.example/pixel.gif?id=…">` を仕込むと、
 * 受信者が本文を開いた瞬間に**送信者のサーバーへ受信者から直接 HTTP が飛ぶ**。
 * 送信者はそれだけで次を手に入れる。
 *
 *   - 受信者の IP アドレス（→ 地域・通信事業者・在宅かモバイルか）
 *   - User-Agent（→ OS・端末・メールクライアント）
 *   - 開封の有無 / 時刻 / 回数（`id=` で受信者個人と結びつく）
 *
 * これは「開封率の計測」として日常的に行われているが、受信者が同意していない
 * 追跡であり、通信の秘密にも触れる。**受信者を守るのはメールサービス側の責務**。
 *
 * ## 3 つのモード
 *
 * `block` … 取りに行かない。最も安全だが画像が出ない。
 * `proxy` … **こちらが代理で取得**して受信者へ渡す（既定）。送信者に見えるのは
 *            我々のサーバーの IP と正規化した UA だけになる。
 * `direct`… 受信者のブラウザが直接取りに行く。従来どおり漏れる。
 *            **これを既定にしない**。契約者が明示的に選んだときだけ。
 *
 * ## プロキシが守る規律（ここが本体）
 *
 * 1. **出口 IP をローテーションする**（`rotate`）。同じ画像を何度取っても
 *    毎回違う IP から出るので、送信者側で「同一受信者が何回開いたか」を
 *    IP で束ねられない。固定 1 本だと「このサービスの利用者が開いた」ことは
 *    分かるので、少なくとも個人の特定からは切り離す。
 * 2. **ヘッダを正規化する。** UA は固定文字列、`Referer` と `Cookie` は送らない、
 *    `Accept-Language` も固定。受信者の言語設定は指紋になる。
 * 3. **リダイレクトを追わない。** 追跡は 302 のチェーンでも成立する（各ホップで
 *    ログが残る）。追わずに 1 回で終える。
 * 4. **キャッシュする。** 一度取った画像は再取得しない。同じ画像を 2 回開いても
 *    送信者側のログは 1 行しか増えない＝**開封回数が漏れない**。
 * 5. **プリフェッチ**（任意）。メール受信の時点で先に取ってしまえば、
 *    ログに載る時刻は「配送時刻」になり、**開封時刻そのものが漏れない**。
 *    ここまでやると開封計測は完全に無効化される。
 * 6. **トラッキングピクセルは取得結果を捨てる。** 1x1 や極小画像、既知の
 *    トラッカーは透明 GIF に差し替える。表示上は変わらない（元も見えない）。
 * 7. **SSRF を通さない。** 取得は必ず `outboundRequest`（毎回 DNS を引き直し、
 *    検査済み IP へ `lookup` を固定する）を通す。ここを素の fetch にすると
 *    メール本文に `http://169.254.169.254/…` を書くだけで
 *    **クラウドのメタデータサービスを読ませられる**。
 *
 * ## このファイルに暗号処理を書かない理由
 *
 * `@mail-sv/shared` は admin / portal（Vite でブラウザへバンドルされる）からも
 * import される。`node:crypto` をここに持ち込むとブラウザ側のビルドが壊れる。
 * 参照値（プロキシ URL に載せる封をした値）の生成と検証は
 * `apps/api/src/services/image-proxy.ts` に置く。ここに置くのは
 * **どちらの面でも同じでなければならない語彙と判定**だけ。
 */
import { z } from 'zod';
import { getDomain, parse as parseHost } from 'tldts';

/* ==================================================== 語彙 */

/** リモート画像の扱い。既定は `proxy`。 */
export const REMOTE_IMAGE_MODE = ['block', 'proxy', 'direct'] as const;
export type RemoteImageMode = (typeof REMOTE_IMAGE_MODE)[number];

/** プロキシの出口 IP の選び方。 */
export const PROXY_EGRESS_MODE = ['fixed', 'rotate'] as const;
export type ProxyEgressMode = (typeof PROXY_EGRESS_MODE)[number];

/**
 * 取得した画像の判定結果。
 *
 * `clean` 以外は**実体を返さず透明 GIF に差し替える**。
 * 「取得はしたが中身は渡さない」ので、送信者のログには 1 回だけ載るが
 * 受信者側には何も表示されない（元々 1x1 は見えないので劣化しない）。
 */
export const TRACKER_VERDICT = [
  'clean',
  /** 1x1 など極小画像。中身に意味が無く、開封計測が唯一の用途。 */
  'tracking_pixel',
  /** 既知の計測事業者のホスト。 */
  'known_tracker',
  /** 取得そのものを断った（SSRF 判定・サイズ超過・content-type 不一致）。 */
  'blocked',
] as const;
export type TrackerVerdict = (typeof TRACKER_VERDICT)[number];

/** プロキシが受け入れる画像の Content-Type。**SVG は入れない。** */
export const PROXY_ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/avif',
  'image/bmp',
  'image/x-icon',
  'image/vnd.microsoft.icon',
] as const;

/**
 * **SVG を許さない理由**: SVG は XML で、`<script>` と外部参照
 * （`<image href>` / `<use href>` / CSS の `url()`）を書ける。
 * プロキシ経由で配ると**自分のオリジンから**スクリプトを配信することになり、
 * Webメールの CSP をすり抜けて本文からセッションを触れる経路になる。
 * ラスタ画像に限定するのは機能の劣化だが、ここは安全側に倒す。
 */
export const PROXY_REJECTED_IMAGE_TYPES = ['image/svg+xml'] as const;

/** 取得を許すスキーム。`data:` と `cid:` は本文内で完結するのでプロキシに来ない。 */
export const PROXY_ALLOWED_SCHEMES = ['http:', 'https:'] as const;

/* ==================================================== 既定値 */

/**
 * プロキシの既定上限。
 *
 * `maxBytes` は 10 MiB。メール中の画像としては十分で、これを超えるものは
 * 装飾ではなく「本文の外にあるもの」なので取りに行かない。
 * 上限が無いと 1 通の本文で数 GB を引かせる嫌がらせが成立する。
 */
export const PROXY_DEFAULTS = {
  maxBytes: 10 * 1024 * 1024,
  timeoutMs: 8_000,
  /** キャッシュ保持。長いほど「開封回数が漏れない」が、更新も反映されない。 */
  cacheTtlSeconds: 7 * 24 * 60 * 60,
  /** これ以下は中身に意味が無いとみなす（1x1 GIF は 35〜43 バイト程度）。 */
  trackingPixelMaxBytes: 512,
  /** 参照値の有効期限。**期限を切らないと URL が永久に生きる**。 */
  refTtlSeconds: 30 * 24 * 60 * 60,
} as const;

/**
 * プロキシが名乗る固定 User-Agent。
 *
 * **受信者の UA を転送しない。** 転送すると端末が漏れる。
 * かつ「プロキシである」ことを明示する（隠して素の UA を騙ると、
 * 相手側の bot 対策を回避する行為になり、こちらの正当性が下がる）。
 */
export const PROXY_USER_AGENT = 'Mozilla/5.0 (compatible; AuroraHostImageProxy/1.0; +https://aurora.host/image-proxy)';

/**
 * 既知トラッカーのホストパターン（初期値）。
 *
 * **これで防ぎ切るつもりはない。** 計測ドメインは無数にあり、追いかけても
 * 追いつかない。本体の守りは「プロキシ経由にすること」であって、
 * この一覧は「明らかに計測専用の宛先は取得すらしない」ための補助。
 * 運営が `tracker_host_rules` で足せるようにしてあるので、ここは種だけ持つ。
 *
 * 形式は**登録可能ドメイン（eTLD+1）または完全なホスト名**。
 * `startsWith` のような部分一致では判定しない（`evil-mailchimp.com` のような
 * 別ドメインを誤って掴むため）。
 */
export const KNOWN_TRACKER_HOSTS = [
  'list-manage.com',
  'mailchimp.com',
  'sendgrid.net',
  'sparkpostmail.com',
  'mandrillapp.com',
  'cmail19.com',
  'createsend.com',
  'hubspotemail.net',
  'hs-sites.com',
  'pardot.com',
  'exct.net',
  'exacttarget.com',
  'mktoresp.com',
  'marketo.com',
  'braze.com',
  'iterable.com',
  'customeriomail.com',
  'klaviyomail.com',
  'omnisend.com',
  'sailthru.com',
  'mailgun.org',
  'postmarkapp.com',
  'amazonses.com',
  'rs6.net',
  'constantcontact.com',
  'openrate.jp',
  'satori.marketing',
] as const;

/* ==================================================== 純関数 */

/**
 * 画像 URL を正規化する。**キャッシュキーの元**になる。
 *
 * ここで潰すのは「同じ画像を指す別表記」だけ。クエリは**残す**
 * （多くの CDN がクエリで画像を切り替えるので、落とすと別画像が出る）。
 * ただしフラグメントは落とす（サーバーへ送られない値なので、
 * 残すとキャッシュが無駄に増える）。
 *
 * 判定不能なもの（スキームが違う・ホストが無い）は `null`。
 * **`null` を「そのまま通す」に倒さないこと。**
 */
export function normalizeImageUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0 || trimmed.length > 4096) return null;

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }
  if (!(PROXY_ALLOWED_SCHEMES as readonly string[]).includes(url.protocol)) return null;
  if (url.hostname.length === 0) return null;
  // 認証情報付き URL は受けない（プロキシに他人の資格情報を持たせない）。
  if (url.username !== '' || url.password !== '') return null;

  url.hash = '';
  // ホストは小文字・末尾ドットなし。既定ポートは省く（URL が正規化してくれる）。
  url.hostname = url.hostname.toLowerCase().replace(/\.$/, '');
  return url.toString();
}

/**
 * ホスト名が既知トラッカーに当たるか。
 *
 * 判定は **eTLD+1 の完全一致**か、**ホスト名の完全一致**。
 * 部分一致にしないのは、`not-sendgrid.net.example.com` のような
 * 別所有者のホストを掴まないため。
 */
export function matchesKnownTracker(
  hostname: string,
  patterns: readonly string[] = KNOWN_TRACKER_HOSTS,
): boolean {
  const host = hostname.toLowerCase().replace(/\.$/, '');
  if (host.length === 0) return false;
  const registrable = getDomain(host) ?? '';
  for (const p of patterns) {
    const pat = p.toLowerCase().replace(/\.$/, '');
    if (pat.length === 0) continue;
    if (host === pat) return true;
    if (registrable !== '' && registrable === pat) return true;
  }
  return false;
}

/** ホスト名が IP リテラルか（`parse()` が `null` の domain を返す）。 */
export function isIpLiteralHost(hostname: string): boolean {
  const r = parseHost(hostname);
  return r.hostname !== null && r.domain === null && r.publicSuffix === null;
}

/**
 * 取得結果が計測専用のピクセルか。
 *
 * サイズだけで判定する。**ピクセル寸法を得るには画像をデコードする必要があり、
 * デコーダを本文由来のバイト列に当てるのは攻撃面**（既知の脆弱性が多い）。
 * 「極端に小さいラスタ画像は装飾として意味を持たない」で足りる。
 */
export function isTrackingPixel(
  byteSize: number,
  contentType: string,
  maxBytes: number = PROXY_DEFAULTS.trackingPixelMaxBytes,
): boolean {
  if (!contentType.toLowerCase().startsWith('image/')) return false;
  return byteSize > 0 && byteSize <= maxBytes;
}

/** Content-Type が受け入れ可能か（パラメータ付きでも判定できる）。 */
export function isAllowedImageType(contentType: string | null | undefined): boolean {
  if (!contentType) return false;
  const mime = contentType.split(';', 1)[0]!.trim().toLowerCase();
  if ((PROXY_REJECTED_IMAGE_TYPES as readonly string[]).includes(mime)) return false;
  return (PROXY_ALLOWED_IMAGE_TYPES as readonly string[]).includes(mime);
}

/**
 * 1x1 の透明 GIF（43 バイト）。
 *
 * トラッキングピクセルと判定したときに**これを返す**。404 にしないのは、
 * クライアント側が「壊れた画像」のアイコンを出して本文が汚れるため。
 * 元々見えない画像なので、透明を返せば見た目は変わらない。
 */
export const TRANSPARENT_GIF_BASE64 = 'R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/* ==================================================== 契約 */

/**
 * 組織のプライバシーポリシー。
 *
 * `direct` を選べるのは「社内向けで計測を許容する」といった判断があるとき。
 * **既定は `proxy`**（何も設定していない契約者が最も守られる状態にする）。
 */
export const orgPrivacyPolicySchema = z.object({
  remoteImageMode: z.enum(REMOTE_IMAGE_MODE).default('proxy'),
  egressMode: z.enum(PROXY_EGRESS_MODE).default('rotate'),
  /** 受信時に先読みして開封時刻を隠す。プラン機能フラグで開く。 */
  prefetchEnabled: z.boolean().default(false),
  /** 1x1 と既知トラッカーを透明 GIF に差し替える。 */
  stripTrackingPixels: z.boolean().default(true),
  /** リンクのクリック時に Referer を送らせない（本文へ `no-referrer` を注入）。 */
  stripReferrer: z.boolean().default(true),
  cacheTtlSeconds: z.number().int().min(0).max(365 * 24 * 60 * 60).default(PROXY_DEFAULTS.cacheTtlSeconds),
  /**
   * ここに挙げたホストは**プロキシせず直接読ませる**。
   * 自社 CDN の画像でプロキシのキャッシュを無駄に埋めたくない場合に使う。
   * 空配列が既定（＝全部プロキシ）。
   */
  bypassHosts: z.array(z.string().min(1).max(253)).max(50).default([]),
});
export type OrgPrivacyPolicy = z.infer<typeof orgPrivacyPolicySchema>;

/** 契約者が更新できる項目。すべて任意（部分更新）。 */
export const orgPrivacyPolicyUpdateSchema = orgPrivacyPolicySchema
  .partial()
  .refine((v) => Object.keys(v).length > 0, '更新する項目がありません');

/**
 * プランの制約を当てた**実効ポリシー**を返す。
 *
 * ここが唯一の合成点。画面と API とワーカーで別々に判定すると、
 * 「画面では有効なのに実際には効いていない」がすぐ生まれる。
 *
 * - `imageProxy` が false のプランでは `proxy` を選べない
 *   （＝プロキシの設備を提供しない契約）。その場合は **`block` に落とす**。
 *   `direct` に落とさないのは、プランの都合で受信者の保護が外れるのは
 *   筋が通らないため。画像を出さないほうがまだ良い。
 * - `imageProxyIpRotation` が false なら出口 IP は固定。
 * - `imageProxyPrefetch` が false ならプリフェッチしない。
 */
export function resolvePrivacyPolicy(
  policy: OrgPrivacyPolicy,
  features: {
    imageProxy: boolean;
    imageProxyIpRotation: boolean;
    imageProxyPrefetch: boolean;
  },
): OrgPrivacyPolicy {
  const mode: RemoteImageMode =
    policy.remoteImageMode === 'proxy' && !features.imageProxy ? 'block' : policy.remoteImageMode;
  return {
    ...policy,
    remoteImageMode: mode,
    egressMode: features.imageProxyIpRotation ? policy.egressMode : 'fixed',
    prefetchEnabled: features.imageProxyPrefetch ? policy.prefetchEnabled : false,
  };
}

/** 既定ポリシー（行が無い組織に対して使う）。 */
export function defaultPrivacyPolicy(): OrgPrivacyPolicy {
  return orgPrivacyPolicySchema.parse({});
}

/* ==================================================== プロキシ URL の規約 */

/**
 * プロキシの公開パス。**面をまたぐ規約なのでここが正。**
 *
 * 本文の書き換え側（`data-src` → プロキシ URL）と、受け取って取得する側
 * （`apps/api/src/routes/webmail-proxy.ts`）と、前段の振り分け
 * （`deploy/vps/site.caddy`）の 3 か所が同じ文字列を前提にする。
 * 1 か所でも書き間違えると「画像が全部出ない」になり、しかも
 * 型検査では気付けないので定数にしておく。
 *
 * 公開面（`/api/v1/public`）に置くのは、**Webメールのセッションを要求しない**
 * ため。`<img>` は Authorization ヘッダを付けられず、Cookie に頼ると
 * 別オリジンからの読み込みで落ちる。参照値そのものが持ち主の証明になる
 * （組織 ID と期限を封じた AEAD なので、他人が作れない）。
 */
export const IMAGE_PROXY_PATH = '/api/v1/public/img';

/** 参照値からプロキシのパスを組み立てる。 */
export function imageProxyPath(ref: string): string {
  return `${IMAGE_PROXY_PATH}/${ref}`;
}

/**
 * プロキシが送るリクエストヘッダ。
 *
 * **受信者に由来する値を 1 つも含めないことが仕様。**
 * ここに `referer` / `cookie` / 受信者の `accept-language` を足すと、
 * プロキシを挟んだ意味がその場で消える。`accept-encoding: identity` は
 * 「圧縮しても画像は縮まない」ことに加え、伸長処理を本文由来のバイト列に
 * 当てないため（zip bomb の面を作らない）。
 */
export function proxyRequestHeaders(): Record<string, string> {
  return {
    'user-agent': PROXY_USER_AGENT,
    accept: 'image/avif,image/webp,image/png,image/jpeg,image/gif;q=0.9,*/*;q=0.5',
    // 受信者の言語設定は指紋になる。固定値だけを送る。
    'accept-language': 'en-US,en;q=0.9',
    'accept-encoding': 'identity',
    // 接続を使い回さない（同一コネクションで束ねられると出口 IP を
    // 変えても「同じクライアント」として追跡できてしまう）。
    connection: 'close',
  };
}

/**
 * プロキシが返す応答ヘッダ。
 *
 * - `Cache-Control: private` … 途中の共有キャッシュに置かせない。置かれると
 *   我々の外に「誰がどの画像を見たか」の複製ができる。
 * - `Referrer-Policy: no-referrer` … 画像から先へ辿らせない。
 * - `Content-Security-Policy: default-src 'none'; sandbox` … 万一 HTML や
 *   SVG が混ざっても実行させない（型は別途弾いているが多層で持つ）。
 * - `X-Content-Type-Options: nosniff` … ブラウザに型を推測させない。
 *   推測を許すと「画像として保存したものが HTML として解釈される」。
 * - `Cross-Origin-Resource-Policy: cross-origin` … Webメール本体と
 *   API のオリジンが分かれている構成でも `<img>` から読めるようにする。
 */
export function proxyResponseHeaders(maxAgeSeconds: number): Record<string, string> {
  const maxAge = Math.max(0, Math.floor(maxAgeSeconds));
  return {
    'cache-control': `private, max-age=${maxAge}`,
    'referrer-policy': 'no-referrer',
    'content-security-policy': "default-src 'none'; sandbox",
    'x-content-type-options': 'nosniff',
    'cross-origin-resource-policy': 'cross-origin',
  };
}

/* ==================================================== 判定（面をまたぐ） */

/** 1 つのリモート画像に対する扱い。 */
export type RemoteImageAction = 'block' | 'direct' | 'proxy';

export interface RemoteImageDecision {
  action: RemoteImageAction;
  /** 正規化済み URL。`block` のときは null（載せるべき URL が無い）。 */
  url: string | null;
  /**
   * 既知トラッカーに当たっている。`proxy` のときは**取得せずに**
   * 透明 GIF を返す判断材料になる（取りに行かないので送信者のログに
   * 1 行も残らない ＝ 開封が完全に隠れる）。
   */
  knownTracker: boolean;
  reason: 'ok' | 'invalid_url' | 'policy_block' | 'policy_direct' | 'bypass_host';
}

/**
 * 本文中の 1 つの画像 URL をどう扱うかを決める。
 *
 * **本文書き換え側とプロキシ側の両方がこれを通す。** 判定を 2 か所に書くと
 * 「書き換えではプロキシにしたのに、取得側は直接に倒す」のような食い違いが
 * 生まれ、受信者から見ると再現しない挙動になる。
 *
 * `policy` は**プランを当てた後の実効ポリシー**（`resolvePrivacyPolicy()` の
 * 返り値）を渡すこと。生のポリシーを渡すと、プランで閉じているはずの
 * プロキシが有効に見える。
 */
export function decideRemoteImage(
  rawUrl: string,
  policy: OrgPrivacyPolicy,
  trackerPatterns: readonly string[] = KNOWN_TRACKER_HOSTS,
): RemoteImageDecision {
  const url = normalizeImageUrl(rawUrl);
  if (url === null) {
    // 判定できないものは通さない。**「そのまま通す」に倒さないこと。**
    return { action: 'block', url: null, knownTracker: false, reason: 'invalid_url' };
  }
  const hostname = new URL(url).hostname;
  const knownTracker = policy.stripTrackingPixels && matchesKnownTracker(hostname, trackerPatterns);

  if (policy.remoteImageMode === 'block') {
    return { action: 'block', url, knownTracker, reason: 'policy_block' };
  }
  if (policy.remoteImageMode === 'direct') {
    return { action: 'direct', url, knownTracker, reason: 'policy_direct' };
  }
  // 契約者が「自社 CDN はプロキシしなくてよい」と挙げたホスト。
  // **トラッカー判定より後ろに置かない**（バイパスに書けば計測を素通しできる、
  // という抜け道を作らないため、トラッカーはバイパスより優先する）。
  if (!knownTracker && matchesKnownTracker(hostname, policy.bypassHosts)) {
    return { action: 'direct', url, knownTracker, reason: 'bypass_host' };
  }
  return { action: 'proxy', url, knownTracker, reason: 'ok' };
}

export interface ProxyFetchClassification {
  verdict: TrackerVerdict;
  /** 実体を保存して返すか。`false` なら透明 GIF を返す。 */
  storeBody: boolean;
  /** 監査・運用ログに残す一次情報（受信者とは結び付けない）。 */
  detail: string;
}

/**
 * 取得結果をどう扱うかを決める。**取得した後の唯一の判断点。**
 *
 * ここを API 側とワーカー側で別々に書くと、同じ画像がキャッシュの作られ方に
 * よって違う扱いになる（＝「たまに画像が出ない」）。
 */
export function classifyProxyFetch(input: {
  status: number;
  contentType: string | null;
  byteSize: number;
  knownTracker: boolean;
  stripTrackingPixels: boolean;
  maxBytes?: number;
  trackingPixelMaxBytes?: number;
}): ProxyFetchClassification {
  const maxBytes = input.maxBytes ?? PROXY_DEFAULTS.maxBytes;
  if (input.knownTracker && input.stripTrackingPixels) {
    return { verdict: 'known_tracker', storeBody: false, detail: '既知の計測ホスト' };
  }
  // **200 以外は保存しない。** 3xx を追わない以上、リダイレクトは失敗として扱う。
  if (input.status !== 200) {
    return { verdict: 'blocked', storeBody: false, detail: `HTTP ${input.status}` };
  }
  if (!isAllowedImageType(input.contentType)) {
    return { verdict: 'blocked', storeBody: false, detail: `不許可の型: ${input.contentType ?? '(なし)'}` };
  }
  if (input.byteSize <= 0) {
    return { verdict: 'blocked', storeBody: false, detail: '空の応答' };
  }
  if (input.byteSize > maxBytes) {
    return { verdict: 'blocked', storeBody: false, detail: `上限超過: ${input.byteSize} バイト` };
  }
  if (
    input.stripTrackingPixels &&
    isTrackingPixel(input.byteSize, input.contentType ?? '', input.trackingPixelMaxBytes)
  ) {
    return { verdict: 'tracking_pixel', storeBody: false, detail: `極小画像: ${input.byteSize} バイト` };
  }
  return { verdict: 'clean', storeBody: true, detail: '' };
}

/* ==================================================== キャッシュの上限 */

/**
 * 画像キャッシュの容量上限（掃除ジョブが使う）。
 *
 * **期限（`cacheTtlSeconds`）だけでは容量が閉じない。** 既定の保持は 7 日で、
 * 契約者は最長 30 日まで延ばせる。受信量の多い組織はその期間ぶんの
 * 画像を丸ごと S3 に積むことになり、**運営が肩代わりしている帯域と保管費が
 * 契約者の受信量に比例して青天井**になる（プロキシは「受信者を守るために
 * 我々が取りに行く」機能なので、費用は必ずこちら側に出る）。
 *
 * 上限に当たったときに捨てるのは**最も長く使われていないもの**
 * （`last_served_at`、無ければ `fetched_at`）。捨てても壊れない
 * （次に表示されたときに取り直すだけ）が、取り直しは送信者のログに
 * 1 行増えることを意味するので、**上限は「事故を止める柵」であって
 * 日常的に当たる値ではない**ことが要点。日常的に当たる値にすると、
 * 「開封回数が漏れない」という機能の中身が静かに失われる。
 */
export const PROXY_CACHE_LIMITS = {
  /** 1 組織あたりの保持バイト数（2 GiB）。 */
  maxBytesPerOrg: 2 * 1024 * 1024 * 1024,
  /** 1 周期で捨てる件数の上限（掃除で S3 を叩き潰さないため）。 */
  evictBatchSize: 500,
} as const;
